import { z } from 'zod'

import { PAYMENT_METHODS } from '#admin/features/billing/payments-api.ts'
import { api } from '#admin/lib/api-client.ts'

/**
 * The payment method catalog. Platform staff add, edit, retire and restore methods here. Each
 * edition turns methods on at Billing > Payment methods.
 */

/** The systems that can open a payment online. Null in a row means a manual method. */
export const PROVIDERS = ['bi_snap'] as const

export const PROVIDER_LABEL: Record<(typeof PROVIDERS)[number], string> = {
  bi_snap: 'BI-SNAP',
}

const catalogMethodSchema = z.object({
  code: z.string(),
  name: z.string(),
  kind: z.enum(PAYMENT_METHODS),
  provider: z.enum(PROVIDERS).nullable(),
  enabledByDefault: z.boolean(),
  sortOrder: z.number(),
  retiredAt: z.string().nullable(),
})

export type CatalogMethod = z.infer<typeof catalogMethodSchema>

export type NewCatalogMethod = Pick<
  CatalogMethod,
  'code' | 'name' | 'kind' | 'provider' | 'enabledByDefault' | 'sortOrder'
>

/** Only these fields change after create. `code`, `kind` and `provider` stay fixed. */
export type CatalogMethodChanges = Partial<
  Pick<CatalogMethod, 'name' | 'enabledByDefault' | 'sortOrder'> & { retired: boolean }
>

const base = '/v1/platform/payment-methods'

export function fetchCatalog(signal?: AbortSignal): Promise<{ items: CatalogMethod[] }> {
  return api.get(base, {
    schema: z.object({ items: z.array(catalogMethodSchema) }),
    ...(signal ? { signal } : {}),
  })
}

export function createCatalogMethod(body: NewCatalogMethod): Promise<CatalogMethod> {
  return api.post(base, { schema: catalogMethodSchema, body })
}

export function updateCatalogMethod(
  code: string,
  changes: CatalogMethodChanges,
): Promise<CatalogMethod> {
  return api.patch(`${base}/${code}`, { schema: catalogMethodSchema, body: changes })
}

/** Every method's code, retired ones too, in the new order. The server refuses any other list. */
export function reorderCatalog(codes: readonly string[]) {
  return api.put(`${base}/order`, { body: { codes }, schema: z.undefined() })
}

/** The code that the API accepts: lower-case letters and digits, joined by single underscores. */
export const CODE_PATTERN = /^[a-z0-9]+(_[a-z0-9]+)*$/

/** A code from a name: "Pay at the venue" → `pay_at_the_venue`. */
export function codeFromName(name: string): string {
  return name
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '_')
    .replaceAll(/^_+|_+$/g, '')
    .slice(0, 40)
    .replace(/_+$/, '')
}
